import React, { useState, useRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import { apiHelper, baseUrl } from "helpers/util";
import { notify } from "helpers/globalFunctions";
import ColumnBox from "components/ColumnBox";
import SmallHeading from "components/SmallHeading";
import { Alert, Snackbar } from "@mui/material";
import RecordDisplay from "./components/RecordDisplay";
import { getFolkLevelLocation } from "config/folkLevelConfig";
import folkLogo from "../../images/folklogo.png";

export default function EditableSundayAttendance() {
    console.log("EditableSundayAttendance is rendering");

    const printerProfile = {
        paperWidthPx: 384,
        paddingPx: 12,
        fontSizePx: 22,
        lineHeightPx: 34,
        logoWidthPx: 100,
        bleChunkSize: 128,
        bleWriteDelayMs: 60,
        bleRetryCount: 2,
        bleRetryDelayMs: 120,
        trailingFeedLines: 2,
        prePrintResetDelayMs: 250,
        postPrintResetDelayMs: 250,
        postPrintDrainMs: 1800,
    };

    const lateLevelsWithFine = ["FOLK Prelims", "FOLK Graduate", "FOLK Enhanced", "FOLK Plus"];

    const guideOptions = [
        { value: "recuyAngLn9kt8cg1", label: "Sahadeva Sakha Dasa" },
        { value: "recoLIhLrvyfSaMHI", label: "Pradyumna Krishna Dasa" },
        { value: "recHrUGhvdM8Hhzym", label: "Braj Bandhu Dasa" },
        { value: "recnoo02iOuDa1C4P", label: "Venkatadri Govinda Dass" },
        { value: "recBYulQ6Lb0f5W6B", label: "Sankarshan Balaram Dasa" },
        { value: "recw42HTRXGr5553V", label: "Paramananda Dasa" },
        { value: "recysZTddnsx28RaA", label: "Saroj Nayana Krishna Dasa" },
        { value: "recTRZpL8Z72JtZOb", label: "Raja Mohan Dasa" },
        { value: "recp8g9DIrGGTo9Ua", label: "Nadiya Chandra Dasa" },
        { value: "reclSHnWl1OJ4Ok7N", label: "Raktaka Govinda Dasa" },
        { value: "recwjlmXLXjFp5hKc", label: "Ramagati Dasa" },
    ];

    const normalizeGuideName = (guideNameRaw) => {
        if (Array.isArray(guideNameRaw)) {
            return (guideNameRaw[0] || "").toString().trim();
        }
        return (guideNameRaw || "").toString().trim();
    };

    const normalizeGuideId = (guideIdRaw) => {
        if (Array.isArray(guideIdRaw)) {
            return (guideIdRaw[0] || "").toString().trim();
        }
        return (guideIdRaw || "").toString().trim();
    };

    const resolveGuideId = (record) => {
        const linkedGuideId = normalizeGuideId(record?.["Linked Guide"]);
        if (linkedGuideId) return linkedGuideId;
        const guideName = normalizeGuideName(record?.["Guide Name"]);
        const matchedGuide = guideOptions.find(
            (g) => g.label.toLowerCase().trim() === guideName.toLowerCase().trim()
        );
        return matchedGuide?.value || "";
    };

    const [mobileNo, setMobileNo] = useState("");
    const [getRecordId, setRecordId] = useState("");
    const [loading, setLoading] = useState(false);
    const [isPrintSubmitting, setIsPrintSubmitting] = useState(false);
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("");

    const formErrorState = useRef({});
    const printState = useRef({ printCharacteristic: null, index: 0, data: null, server: null });
    const searchTimeoutRef = useRef(null);
    const lastSearchedValueRef = useRef("");
    const isValidMobileNumber = (value) => /^\d{10}$/.test(String(value || "").trim());
    const isQrSearchValue = (value) => /^rec[A-Za-z0-9]{14}$/.test(String(value || "").trim());
    const getDateStr = () => new Date().toLocaleDateString("en-IN");
    const getTimeStr = () => new Date().toLocaleTimeString("en-IN", { hour12: true });
    const getAndroidPrinterBridge = () => {
        if (typeof window === "undefined") {
            return null;
        }
        const bridge = window.AndroidApp;
        return bridge && typeof bridge.printPrasadam === "function" ? bridge : null;
    };

    const showPageAlert = (text, severity = "error") => {
        setErrorMessage(text);
        setErrorColor(severity);
    };

    // State for editing
    const [editState, setEditState] = useState({
        name: false,
        mobileNo: false,
        folkLevel: false,
        folkGuide: false,
        occupation: false,
        folkCentre: false,
        areaOfStay: false,
        chantingRounds: false,
        ashrayaLevel: false,
        courseMaterial: false,
        paymentScreenshot: false,
        paymentAmount: false,
    });
    const [editedRecord, setEditedRecord] = useState({
        name: "",
        mobileNo: "",
        folkLevel: "",
        folkGuide: "",
        occupation: "",
        folkCentre: "",
        areaOfStay: "",
        chantingRounds: "",
        ashrayaLevel: "",
        courseMaterial: "",
        paymentScreenshot: null,
        paymentAmount: "",
    });

    const resetForm = () => {
        try {
            // Reset all form states
            setMobileNo("");
            setFetchedRecord(null);
            setErrorMessage(null);
            setErrorColor("");
            setRecordId("");
            setAreOptionsLoading(false);
            setIsPrintSubmitting(false);

            // Reset edit states
            setEditState({
                name: false,
                mobileNo: false,
                folkLevel: false,
                folkGuide: false,
                occupation: false,
                folkCentre: false,
                areaOfStay: false,
                chantingRounds: false,
                ashrayaLevel: false,
                courseMaterial: false,
                paymentScreenshot: false,
                paymentAmount: false,
            });

            // Reset edited record
            setEditedRecord({
                name: "",
                mobileNo: "",
                folkLevel: "",
                folkGuide: "",
                occupation: "",
                folkCentre: "",
                areaOfStay: "",
                chantingRounds: "",
                ashrayaLevel: "",
                courseMaterial: "",
                paymentScreenshot: null,
                paymentAmount: "",
            });

            // Clear form error state
            formErrorState.current = {};
            lastSearchedValueRef.current = "";
            printState.current = {
                ...printState.current,
                index: 0,
                data: null,
            };

            console.log("Form reset completed successfully");
        } catch (error) {
            console.error("Error in resetForm:", error);
        }
    };

    const resetDom = () => {
        try {
            setFetchedRecord(null);
            setErrorMessage(null);
            setErrorColor("");

            // Reset edit states and edited record to prevent stale data
            setEditState({
                name: false,
                mobileNo: false,
                folkLevel: false,
                folkGuide: false,
                occupation: false,
                folkCentre: false,
                areaOfStay: false,
                chantingRounds: false,
                ashrayaLevel: false,
                courseMaterial: false,
                paymentScreenshot: false,
                paymentAmount: false,
            });

            setEditedRecord({
                name: "",
                mobileNo: "",
                folkLevel: "",
                folkGuide: "",
                occupation: "",
                folkCentre: "",
                areaOfStay: "",
                chantingRounds: "",
                ashrayaLevel: "",
                courseMaterial: "",
                paymentScreenshot: null,
                paymentAmount: "",
            });
            console.log("DOM reset completed successfully");
        } catch (error) {
            console.error("Error in resetDom:", error);
        }
    };

    const searchByMobileNo = async (mobileNo) => {
        const searchValue = String(mobileNo || "").trim();
        if ((!isValidMobileNumber(searchValue) && !isQrSearchValue(searchValue)) || isPrintSubmitting) {
            return;
        }

        try {
            setAreOptionsLoading(true);
            setErrorMessage(null);
            resetDom();
            lastSearchedValueRef.current = searchValue;

            // Log the request details for debugging
            console.log("Making API request with mobile:", searchValue);
            const response = await apiHelper.get({
                url: `${baseUrl}/api/editable_sunday_attendance`,
                params: { search_text: searchValue },
            });

            // Log the response for debugging
            console.debug("API Response:", JSON.stringify(response));

            if (response.status) {
                if (!response.json) {
                    throw new Error("Response json is undefined");
                }
                if (response.json.length === 0) {
                    setErrorMessage("No Records found for this Mobile Number.");
                    setErrorColor("error"); // Material-UI color for error messages
                    return; // Early exit if no records found
                }
                const record = response.json[0];
                setRecordId(record["Record ID"]);
                setFetchedRecord(record);
            } else {
                setErrorMessage(`API returned status false: ${JSON.stringify(response.json)}`);
                setErrorColor("error");
            }
        } catch (error) {
            // Detailed error logging
            console.error("Error in searchByMobileNo:", {
                message: error.message,
                stack: error.stack,
                line: error.lineNumber,
                fileName: error.fileName,
            });
            setErrorMessage("An error occurred while searching.");
            setErrorColor("error");
        } finally {
            setAreOptionsLoading(false);
        }
    };

    const handleMobileChange = (e) => {
        const val = e.target.value;
        setMobileNo(val);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const trimmedVal = val.trim();
        if (isValidMobileNumber(trimmedVal) || isQrSearchValue(trimmedVal)) {
            searchTimeoutRef.current = setTimeout(() => {
                if (lastSearchedValueRef.current !== trimmedVal) {
                    searchByMobileNo(trimmedVal);
                }
            }, 250);
        }
    };

    const checkForFormErrors = (field, isError) => {
        formErrorState.current[field] = isError;
        return isError;
    };

    const handleEdit = (field) => {
        setEditState((prevState) => ({ ...prevState, [field]: !prevState[field] }));

        setEditedRecord((prev) => ({
            ...prev,
            name: prev.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || "",
            mobileNo: prev.mobileNo || mobileNo || fetchedRecord["Mobile No."] || "",
            folkLevel: prev.folkLevel || fetchedRecord["FOLK Level (Display)"] || "",
            folkGuide: prev.folkGuide || resolveGuideId(fetchedRecord),
            ashrayaLevel: prev.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "",
            courseMaterial: prev.courseMaterial || fetchedRecord["Course Material"] || "",
            occupation: prev.occupation || fetchedRecord["Occupation"] || "",
            folkCentre: prev.folkCentre || fetchedRecord["FOLK Centre"] || "",
            areaOfStay: prev.areaOfStay || fetchedRecord["Area of Stay"] || "",
            chantingRounds: prev.chantingRounds || fetchedRecord["Chanting"] || "",
            paymentAmount: prev.paymentAmount || fetchedRecord["GFY Payment"] || "",
            paymentScreenshot: prev.paymentScreenshot || null,
        }));
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        console.log("handleChange called:", { name, value, files, type: typeof value });

        setEditedRecord((prev) => {
            let fieldValue = value;

            if (name === "paymentScreenshot") {
                // Handle File object from direct file input
                if (files && files[0]) {
                    fieldValue = files[0];
                }
                // Handle base64 data from RecordDisplay component
                else if (value && typeof value === "object" && value.base64) {
                    fieldValue = value;
                }
            }

            const updated = {
                ...prev,
                [name]: fieldValue,
            };
            console.log("Updated editedRecord:", updated);
            return updated;
        });
    };

    const handleUpdate = () => {
        console.log("Record Id: ", getRecordId);
        setLoading(true);

        const currentFolkLevel = editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"];
        const isGFYLevel = ["GFY Attended", "GFY Registered", "AOMC Attended"].includes(currentFolkLevel);

        // Check if file upload is needed
        const hasFileUpload =
            editState.paymentScreenshot &&
            editedRecord.paymentScreenshot &&
            (editedRecord.paymentScreenshot instanceof File ||
                (typeof editedRecord.paymentScreenshot === "object" && editedRecord.paymentScreenshot.base64));

        if (hasFileUpload) {
            // Use FormData for file upload
            const formData = new FormData();
            formData.append("recordId", getRecordId);

            // Add edited fields
            if (editState.name)
                formData.append(
                    "name",
                    editedRecord.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || ""
                );
            if (editState.folkLevel)
                formData.append("folkLevel", editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"] || "");
            if (editState.folkGuide)
                formData.append("folkGuide", editedRecord.folkGuide || fetchedRecord["Linked Guide"]?.[0] || "");

            if (isGFYLevel) {
                if (editState.ashrayaLevel)
                    formData.append(
                        "ashrayaLevel",
                        editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || ""
                    );
                if (editState.courseMaterial)
                    formData.append(
                        "courseMaterial",
                        editedRecord.courseMaterial || fetchedRecord["Course Material"] || ""
                    );
                if (editState.occupation)
                    formData.append("occupation", editedRecord.occupation || fetchedRecord["Occupation"] || "");
                if (editState.folkCentre)
                    formData.append("folkCentre", editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "");
                if (editState.areaOfStay)
                    formData.append("areaOfStay", editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "");
                if (editState.chantingRounds)
                    formData.append(
                        "chantingRounds",
                        editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || ""
                    );
                if (editState.paymentAmount)
                    formData.append("paymentAmount", editedRecord.paymentAmount || fetchedRecord["GFY Payment"] || "");
            } else {
                formData.append(
                    "ashrayaLevel",
                    editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || ""
                );
                formData.append(
                    "courseMaterial",
                    editedRecord.courseMaterial || fetchedRecord["Course Material"] || ""
                );
                formData.append("occupation", editedRecord.occupation || fetchedRecord["Occupation"] || "");
                formData.append("folkCentre", editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "");
                formData.append("areaOfStay", editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "");
                formData.append(
                    "chantingRounds",
                    editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || ""
                );
            }

            // Add file
            if (editedRecord.paymentScreenshot instanceof File) {
                formData.append("paymentScreenshot", editedRecord.paymentScreenshot);
            } else if (editedRecord.paymentScreenshot.base64) {
                // Convert base64 to blob for FormData
                const base64Data = editedRecord.paymentScreenshot.base64.split(",")[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: editedRecord.paymentScreenshot.type });
                formData.append("paymentScreenshot", blob, editedRecord.paymentScreenshot.filename);
            }

            console.log("Uploading with FormData");

            // File upload request
            apiHelper
                .patch({
                    url: `${baseUrl}/api/editable_sunday_attendance`,
                    data: formData,
                })
                .then(async (response) => {
                    if (response.status && response.json?.records) {
                        notify.success({ description: "Record updated successfully!" });
                        setFetchedRecord(response.json.records[0].fields);
                        resetEditStates();
                    } else {
                        console.error("Error updating record:", response);
                        notify.error({ description: "Failed to update record. Please try again." });
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error updating record:", error);
                    notify.error({ description: "Failed to update record. Please try again." });
                    setLoading(false);
                });
        } else {
            // Regular JSON update
            const payload = { recordId: getRecordId };

            if (editState.name)
                payload.name = editedRecord.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || "";
            if (editState.folkLevel)
                payload.folkLevel = editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"] || "";
            if (editState.folkGuide)
                payload.folkGuide = editedRecord.folkGuide || fetchedRecord["Linked Guide"]?.[0] || "";

            if (isGFYLevel) {
                if (editState.ashrayaLevel)
                    payload.ashrayaLevel = editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "";
                if (editState.courseMaterial)
                    payload.courseMaterial = editedRecord.courseMaterial || fetchedRecord["Course Material"] || "";
                if (editState.occupation)
                    payload.occupation = editedRecord.occupation || fetchedRecord["Occupation"] || "";
                if (editState.folkCentre)
                    payload.folkCentre = editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "";
                if (editState.areaOfStay)
                    payload.areaOfStay = editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "";
                if (editState.chantingRounds)
                    payload.chantingRounds =
                        editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || "";
                if (editState.paymentAmount)
                    payload.paymentAmount = editedRecord.paymentAmount || fetchedRecord["GFY Payment"] || "";

                // Always include paymentAmount if it exists in editedRecord
                if (editedRecord.paymentAmount) payload.paymentAmount = editedRecord.paymentAmount;
            } else {
                payload.ashrayaLevel = editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "";
                payload.courseMaterial = editedRecord.courseMaterial || fetchedRecord["Course Material"] || "";
                payload.occupation = editedRecord.occupation || fetchedRecord["Occupation"] || "";
                payload.folkCentre = editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "";
                payload.areaOfStay = editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "";
                payload.chantingRounds =
                    editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || "";
            }

            console.log("Updating record with payload:", payload);

            apiHelper
                .patch({
                    url: `${baseUrl}/api/editable_sunday_attendance`,
                    data: payload,
                })
                .then(async (response) => {
                    if (response.status && !response.json?.error) {
                        const updatedFields = response.json?.records?.[0]?.fields;

                        console.log("Updated fields from Airtable:", updatedFields);
                        notify.success({ description: "Record updated successfully!" });

                        setFetchedRecord(response.json);
                        resetEditStates();
                    } else {
                        console.error("Error updating record:", response);
                        notify.error({ description: "Failed to update record. Please try again." });
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error updating record:", error);
                    notify.error({ description: "Failed to update record. Please try again." });
                    setLoading(false);
                });
        }
    };

    const resetEditStates = () => {
        setEditState({
            name: false,
            mobileNo: false,
            folkLevel: false,
            folkGuide: false,
            occupation: false,
            folkCentre: false,
            chantingRounds: false,
            ashrayaLevel: false,
            courseMaterial: false,
            areaOfStay: false,
            paymentAmount: false,
            paymentScreenshot: false,
        });
        setEditedRecord({});
    };

    const sleep = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

    const loadSlipLogo = () =>
        new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => resolve(null);
            image.src = folkLogo;
        });

    const writeChunkToPrinter = async (chunk) => {
        const characteristic = printState.current.printCharacteristic;
        if (!characteristic) {
            throw new Error("Printer characteristic unavailable.");
        }

        if (typeof characteristic.writeValueWithResponse === "function") {
            await characteristic.writeValueWithResponse(chunk);
            return;
        }

        await characteristic.writeValue(chunk);
    };

    const resetBlePrinter = async () => {
        const ESC = 0x1b;
        const GS = 0x1d;
        const LF = 0x0a;

        const resetSequence = new Uint8Array([
            ESC, 0x40,
            ESC, 0x32,
            ESC, 0x61, 0x00,
            LF, LF,
            GS, 0x56, 0x00,
            ESC, 0x40,
        ]);

        await writeChunkToPrinter(resetSequence);
    };

    const ensurePrinterConnected = async () => {
        const androidPrinterBridge = getAndroidPrinterBridge();
        if (androidPrinterBridge) {
            return true;
        }

        if (printState.current.printCharacteristic && printState.current.server && printState.current.server.connected) {
            return true;
        }

        if (!navigator.bluetooth) {
            showPageAlert("Web Bluetooth is not supported in this app/browser. Open this page in a BLE-supported browser to connect printer.", "error");
            return false;
        }

        showPageAlert("Searching for printer...", "info");
        try {
            const device = await navigator.bluetooth.requestDevice({ filters: [{ services: ["000018f0-0000-1000-8000-00805f9b34fb"] }] });
            const server = await device.gatt.connect();
            printState.current.server = server;
            const service = await server.getPrimaryService("000018f0-0000-1000-8000-00805f9b34fb");
            const characteristic = await service.getCharacteristic("00002af1-0000-1000-8000-00805f9b34fb");
            printState.current.printCharacteristic = characteristic;
            showPageAlert("Printer connected successfully.", "success");
            return true;
        } catch (err) {
            printState.current.printCharacteristic = null;
            printState.current.server = null;
            if (err && err.name === "NotFoundError") {
                showPageAlert("Printer selection cancelled.", "warning");
            } else {
                showPageAlert(`Bluetooth error: ${err.message}`, "error");
            }
            return false;
        }
    };

    const buildSlipPrintData = async (record, isLate, isLateWithFine, dStr, tStr, isDuplicate = false, outputType = "escpos") => {
        const ESC = 0x1b;
        const LF = 0x0a;
        const GS = 0x1d;
        const W = printerProfile.paperWidthPx;
        const PAD = printerProfile.paddingPx;
        const FS = printerProfile.fontSizePx;
        const LH = printerProfile.lineHeightPx;
        const BOLD = `bold ${FS}px Arial`;
        const NORMAL = `${FS}px Arial`;
        const SMALL = "18px Arial";

        const logoImg = await loadSlipLogo();
        const fields = [
            ["Name:", String(record["Name"] || record["HK Japa User Name"] || "")],
            ["F. Level:", String(record["FOLK Level (Display)"] || "")],
            ["Location:", String(getFolkLevelLocation(record["FOLK Level (Display)"]) || "")],
            ["A. Level:", String(record["Current Ashraya Level"] || "")],
            ["F. Guide:", String(record["Guide Name"] || "")],
            ["Guide No.:", String(record["Guide Mobile No."] || "")],
            ["Date:", String(dStr || "")],
            ["Time:", String(tStr || "").toUpperCase()],
        ];

        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        measureCtx.font = BOLD;
        const labelW = measureCtx.measureText("Guide No.:  ").width;
        const valW = W - PAD - labelW - PAD;

        const wrapLines = (ctx, text, maxW) => {
            const words = String(text || "").split(" ");
            const lines = [];
            let currentLine = "";

            for (const word of words) {
                const nextLine = currentLine ? `${currentLine} ${word}` : word;
                if (ctx.measureText(nextLine).width > maxW && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = nextLine;
                }
            }

            if (currentLine) {
                lines.push(currentLine);
            }

            return lines.length ? lines : [""];
        };

        measureCtx.font = NORMAL;
        const logoH = logoImg ? Math.round((logoImg.naturalHeight / logoImg.naturalWidth) * printerProfile.logoWidthPx) : 0;
        const fineLines = isLateWithFine ? 4 : 0;
        const duplicateHeaderLines = isDuplicate ? 1 : 0;

        let H = PAD + (logoImg ? logoH + 16 : 0) + LH + LH + 28
            + (isDuplicate ? duplicateHeaderLines * LH + 28 : 0)
            + (isLate ? LH + 28 : 0)
            + (isLateWithFine ? fineLines * LH + 28 : 0) + 16;

        for (const [, value] of fields) {
            H += wrapLines(measureCtx, value, valW).length * LH + 12;
        }

        H += 28 + LH + LH + 20 + LH + LH + LH * 2 + PAD;

        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "#000";
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1;

        let y = PAD;

        if (logoImg) {
            ctx.drawImage(logoImg, (W - printerProfile.logoWidthPx) / 2, y, printerProfile.logoWidthPx, logoH);
            y += logoH + 16;
        }

        ctx.font = BOLD;
        ctx.textAlign = "center";
        ctx.fillText("FOLK SUNDAY MEET-UP", W / 2, y + 32);
        y += LH;
        ctx.fillText("ATTENDANCE", W / 2, y + 32);
        y += LH;

        ctx.font = NORMAL;
        ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
        y += 28;

        if (isDuplicate) {
            ctx.font = "bold 26px Arial";
            ctx.fillText(isLate ? "DUPLICATE LATE ATTENDANCE" : "DUPLICATE ATTENDANCE", W / 2, y + 32);
            y += LH;
            ctx.font = NORMAL;
            ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
            y += 28;
        }

        if (isLate && !isDuplicate) {
            ctx.font = BOLD;
            ctx.fillText("LATE ATTENDANCE", W / 2, y + 32);
            y += LH;
            ctx.font = NORMAL;
            ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
            y += 28;
        }

        if (isLateWithFine) {
            ctx.setLineDash([8, 5]);
            ctx.strokeRect(PAD, y, W - PAD * 2, fineLines * LH + 14);
            ctx.setLineDash([]);
            ctx.font = SMALL;
            ctx.textAlign = "center";
            ctx.fillText("Please collect your", W / 2, y + 24);
            ctx.fillText("paid prasadam coupon (Rs. 50)", W / 2, y + 24 + LH);
            ctx.fillText("from the FOLK Front Desk", W / 2, y + 24 + LH * 2);
            ctx.fillText("after 7:00 PM.", W / 2, y + 24 + LH * 3);
            y += fineLines * LH + 28;
        }

        y += 16;
        ctx.textAlign = "left";
        for (const [label, value] of fields) {
            ctx.font = BOLD;
            ctx.fillText(label, PAD, y + FS);
            ctx.font = NORMAL;
            const lines = wrapLines(ctx, value, valW);
            lines.forEach((line, index) => {
                ctx.fillText(line, PAD + labelW, y + FS + index * LH);
            });
            y += lines.length * LH + 12;
        }

        ctx.font = NORMAL;
        ctx.textAlign = "center";
        ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
        y += 28;
        ctx.font = BOLD;
        ctx.fillText("Hare Krishna !!", W / 2, y + 32);
        y += LH;
        ctx.fillText("Have a nice day", W / 2, y + 32);
        y += LH + 20;
        ctx.textAlign = "left";
        ctx.font = "600 34px Arial";
        ctx.fillText("Sign", PAD, y + 40);
        y += LH + 8;
        ctx.font = NORMAL;
        ctx.fillText("(FOLK Guide)", PAD, y + FS);
        y += LH;

        const usedHeight = Math.max(1, Math.min(H, Math.ceil(y + PAD)));

        if (outputType === "jpeg") {
            const outputCanvas = document.createElement("canvas");
            outputCanvas.width = W;
            outputCanvas.height = usedHeight;
            const outputCtx = outputCanvas.getContext("2d");
            outputCtx.fillStyle = "#fff";
            outputCtx.fillRect(0, 0, W, usedHeight);
            outputCtx.drawImage(canvas, 0, 0, W, usedHeight, 0, 0, W, usedHeight);
            const jpegBase64 = outputCanvas.toDataURL("image/jpeg", 0.95).split(";base64,")[1];

            outputCtx.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
            outputCanvas.width = 0;
            outputCanvas.height = 0;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;
            return jpegBase64;
        }

        const { data } = ctx.getImageData(0, 0, W, usedHeight);
        const bytesPerRow = Math.ceil(W / 8);
        const raster = [];

        for (let row = 0; row < usedHeight; row++) {
            for (let byteIndex = 0; byteIndex < bytesPerRow; byteIndex++) {
                let currentByte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    const pixelX = byteIndex * 8 + bit;
                    if (pixelX < W) {
                        const pixelIndex = (row * W + pixelX) * 4;
                        const luminance = data[pixelIndex] * 0.299 + data[pixelIndex + 1] * 0.587 + data[pixelIndex + 2] * 0.114;
                        if (luminance < 128) {
                            currentByte |= 0x80 >> bit;
                        }
                    }
                }
                raster.push(currentByte);
            }
        }

        const xL = bytesPerRow & 0xff;
        const xH = (bytesPerRow >> 8) & 0xff;
        const yL = usedHeight & 0xff;
        const yH = (usedHeight >> 8) & 0xff;

        const trailer = new Array(printerProfile.trailingFeedLines).fill(LF);
        const escPosData = new Uint8Array([
            ESC, 0x40,
            ESC, 0x32,
            ESC, 0x61, 0x01,
            GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...raster,
            ...trailer,
        ]);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        return escPosData;
    };

    const sendEscPosData = async (data) => {
        const CHUNK = printerProfile.bleChunkSize;
        await resetBlePrinter();
        await sleep(printerProfile.prePrintResetDelayMs);

        for (let i = 0; i < data.length; i += CHUNK) {
            const chunk = data.slice(i, i + CHUNK);
            let attempt = 0;
            while (attempt <= printerProfile.bleRetryCount) {
                try {
                    await writeChunkToPrinter(chunk);
                    if (i + CHUNK < data.length && printerProfile.bleWriteDelayMs > 0) {
                        await sleep(printerProfile.bleWriteDelayMs);
                    }
                    break;
                } catch (chunkErr) {
                    attempt += 1;
                    if (attempt > printerProfile.bleRetryCount) {
                        throw chunkErr;
                    }
                    await sleep(printerProfile.bleRetryDelayMs);
                }
            }
        }

        await sleep(printerProfile.bleWriteDelayMs * 2);
        await resetBlePrinter();
        await sleep(printerProfile.postPrintResetDelayMs);
        await sleep(printerProfile.postPrintDrainMs);
    };

    const handlePrintSlip = async () => {
        if (!fetchedRecord) {
            showPageAlert("No record loaded. Search a mobile number first.", "error");
            return;
        }

        if (Number(fetchedRecord["Count"]) > 2) {
            showPageAlert("Warning: this attendee count is more than two.", "warning");
        }

        if (isPrintSubmitting) {
            showPageAlert("Previous print job is still in progress. Please wait.", "warning");
            return;
        }

        const isConnected = await ensurePrinterConnected();
        if (!isConnected) {
            return;
        }

        const now = new Date();
        const isLate = now.getHours() > 16 || (now.getHours() === 16 && now.getMinutes() >= 30);
        const level = fetchedRecord["FOLK Level (Display)"];
        const isLateWithFine = isLate && lateLevelsWithFine.includes(level);
        const isDuplicate = fetchedRecord["Today attended ?"] === 1;

        const dateStr = now.toLocaleString("en-IN", { month: "long", day: "2-digit" });
        const timeStr = now.toLocaleString("en-IN", { hour12: true, hour: "2-digit", minute: "2-digit" });
        const androidPrinterBridge = getAndroidPrinterBridge();

        try {
            setIsPrintSubmitting(true);
            showPageAlert("Building print data...", "success");

            if (androidPrinterBridge) {
                const jpegBase64 = await buildSlipPrintData(fetchedRecord, isLate, isLateWithFine, dateStr, timeStr, isDuplicate, "jpeg");
                androidPrinterBridge.printPrasadam(jpegBase64);
                await sleep(printerProfile.postPrintDrainMs);
                showPageAlert("Print sent successfully!", "success");
            } else {
                const escPosData = await buildSlipPrintData(fetchedRecord, isLate, isLateWithFine, dateStr, timeStr, isDuplicate, "escpos");
                await sendEscPosData(escPosData);
                showPageAlert("Print sent successfully!", "success");
            }

            if (isDuplicate) {
                showPageAlert("Duplicate attendance detected. Slip printed without marking attendance.", "warning");
            } else {
                const markSuccess = await markAttendance(getRecordId);

                if (markSuccess && isLateWithFine) {
                    await saveLateAttendancePrasadam(fetchedRecord, timeStr);
                }
            }
        } catch (error) {
            console.error("Error in handlePrintSlip:", error);
            showPageAlert(`Print failed: ${error.message || error}`, "error");
        } finally {
            setIsPrintSubmitting(false);
            resetForm();
        }
    };

    const saveLateAttendancePrasadam = async (record, timeStr) => {
        try {
            const rawName = record["HK Japa User Name"] || record["Name"] || "";
            const rawMobile = record["Mobile No."] || record["Mobile No"] || "";
            const rawGuide = record["Guide Name"] || record["Linked Guide"] || "";
            const rawLevel = record["FOLK Level (Display)"] || record["FOLK Level"] || "";
            const rawEmail = record["Mail ID"] || record["Email"] || "";

            const payload = {
                name: Array.isArray(rawName) ? rawName[0] : rawName,
                mobileNo: Array.isArray(rawMobile) ? rawMobile[0] : rawMobile,
                folkGuide: Array.isArray(rawGuide) ? rawGuide[0] : rawGuide,
                folkLevel: Array.isArray(rawLevel) ? rawLevel[0] : rawLevel,
                email: Array.isArray(rawEmail) ? rawEmail[0] : rawEmail,
                amount: 50,
                paymentStatus: "pending",
                attendanceTime: new Date().toISOString(),
                transactionId: ""
            };

            await apiHelper.post({
                url: `${baseUrl}/api/late_attendance_payment`,
                data: payload,
            });
            console.log("Late attendance prasadam donation record saved.");
        } catch (error) {
            console.error("Failed to save late attendance prasadam donation:", error);
        }
    };

    const markAttendance = async (recordId) => {
        const payload = { record_id: Array.isArray(recordId) ? recordId[0] : recordId };
        try {
            const response = await apiHelper.post({
                url: `${baseUrl}/api/marked_attendance`,
                data: payload,
            });
            if (response.status) {
                showPageAlert("Attendance recorded successfully!", "success");
                return true;
            }
            showPageAlert("Failed to record attendance. Please try again.", "error");
            return false;
        } catch (error) {
            showPageAlert("Something went wrong while recording attendance.", "error");
            return false;
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", padding: "15px" }}>
            <ColumnBox>
                <SmallHeading sx={{ alignSelf: "center" }}>FOLK Sunday Program</SmallHeading>
                <ColumnBox sx={{ alignItems: "center", maxWidth: "500px", width: "100%" }}>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "460px",
                            marginTop: "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "10px",
                            marginInline: "auto",
                        }}
                    >
                        <TextField
                            required
                            label="Mobile No. / Scan QR"
                            autoFocus
                            size="small"
                            value={mobileNo}
                            onChange={handleMobileChange}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                },
                            }}
                        />

                        <Box sx={{ width: "32px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {areOptionsLoading ? <CircularProgress size="1.5rem" /> : <MobileFriendlyIcon />}
                        </Box>
                    </Box>

                    {errorMessage && (
                        <Snackbar
                            open={!!errorMessage}
                            autoHideDuration={8000}
                            onClose={() => setErrorMessage(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Center the Snackbar
                        >
                            <Alert severity={errorColor} onClose={() => setErrorMessage(null)} sx={{ width: "100%" }}>
                                {errorMessage}
                            </Alert>
                        </Snackbar>
                    )}

                    <RecordDisplay
                        fetchedRecord={fetchedRecord}
                        editState={editState}
                        editedRecord={editedRecord}
                        loading={loading}
                        onEdit={handleEdit}
                        onChange={handleChange}
                        onUpdate={handleUpdate}
                        getDateStr={getDateStr}
                        getTimeStr={getTimeStr}
                    />

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "460px",
                            marginTop: "18px",
                            marginInline: "auto",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handlePrintSlip}
                            disabled={!fetchedRecord || areOptionsLoading || isPrintSubmitting}
                            sx={{
                                width: "calc(100% - 42px)",
                                maxWidth: "418px",
                            }}
                        >
                            {isPrintSubmitting ? <CircularProgress size="1.5rem" color="inherit" /> : "Print Slip"}
                        </Button>
                    </Box>
                </ColumnBox>
            </ColumnBox>
        </Box>
    );
}
